import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Eye, ShoppingCart, CreditCard, CheckCircle2, Clock, XCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { projectTypeLabels } from "./labels";
import { zoneLabel } from "@/i18n/zones";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("fr-BE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

const TxStatus = ({ tx }) => {
  if (tx.payment_status === "confirmed") {
    return <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full"><CheckCircle2 className="w-3 h-3" />Stripe confirmé</span>;
  }
  if (tx.payment_status === "pending") {
    return <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full"><Clock className="w-3 h-3" />En attente</span>;
  }
  return <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 bg-red-100 px-2 py-0.5 rounded-full"><XCircle className="w-3 h-3" />{tx.payment_status}</span>;
};

const Section = ({ icon: Icon, title, count, children, testId }) => (
  <div data-testid={testId}>
    <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900 mb-2">
      <Icon className="w-4 h-4 text-orange-500" />{title}
      <span className="text-xs font-normal text-slate-400">({count})</span>
    </h3>
    {children}
  </div>
);

const LeadLine = ({ lead, extra }) => (
  <div className="flex items-center justify-between gap-3 py-2 border-b border-slate-100 last:border-0 text-sm">
    <div className="min-w-0">
      <p className="font-medium text-slate-900 truncate">
        <span className="font-mono text-xs text-orange-600 mr-2">{lead.lead_number || "—"}</span>
        {projectTypeLabels[lead.project_type] || lead.project_type}
      </p>
      <p className="text-xs text-slate-500 truncate">{lead.city} ({lead.postal_code}) · {lead.budget}{lead.first_name ? ` · ${lead.first_name} ${lead.last_name}` : ""}</p>
    </div>
    <div className="text-right shrink-0">{extra}</div>
  </div>
);

export const ContractorActivityDialog = ({ contractor, onClose, API }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!contractor) return;
    setLoading(true);
    setData(null);
    axios.get(`${API}/contractors/${contractor.id}/activity`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => setData({ error: true }))
      .finally(() => setLoading(false));
  }, [contractor, API]);

  return (
    <Dialog open={!!contractor} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="contractor-activity-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{contractor?.company_name}</DialogTitle>
          <DialogDescription>{contractor?.email} · {contractor?.phone} · Catégories : {contractor?.project_types?.map((k) => projectTypeLabels[k] || k).join(", ") || "—"}<br />Zones : {contractor?.service_areas?.map((z) => zoneLabel(z)).join(", ") || "—"}</DialogDescription>
        </DialogHeader>

        {loading && <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-orange-500" /></div>}
        {data?.error && <p className="text-sm text-red-600">Impossible de charger l'activité.</p>}

        {data && !data.error && (
          <div className="space-y-6">
            <div className="grid grid-cols-3 gap-3">
              {[
                ["Leads vus", data.viewed_leads.length, "activity-kpi-viewed"],
                ["Leads achetés", data.purchased_leads.length, "activity-kpi-purchased"],
                ["Total payé", `${data.total_paid.toFixed(2)}€`, "activity-kpi-paid"],
              ].map(([label, value, tid]) => (
                <div key={label} className="bg-slate-50 rounded-xl p-3 text-center" data-testid={tid}>
                  <p className="text-xs text-slate-500">{label}</p>
                  <p className="text-xl font-heading font-bold text-slate-900">{value}</p>
                </div>
              ))}
            </div>

            <Section icon={ShoppingCart} title="Leads achetés" count={data.purchased_leads.length} testId="activity-purchased">
              {data.purchased_leads.length === 0 ? <p className="text-sm text-slate-400">Aucun achat pour le moment.</p> :
                data.purchased_leads.map((l) => <LeadLine key={l.id} lead={l} extra={<span className="text-sm font-semibold text-green-600">{l.price}€</span>} />)}
            </Section>

            <Section icon={Eye} title="Leads vus sans achat" count={data.viewed_leads.length} testId="activity-viewed">
              {data.viewed_leads.length === 0 ? <p className="text-sm text-slate-400">Aucune consultation.</p> :
                data.viewed_leads.map((l) => <LeadLine key={l.id} lead={l} extra={<span className="text-xs text-slate-400">{fmtDate(l.created_at)}</span>} />)}
            </Section>

            <Section icon={CreditCard} title="Paiements Stripe" count={data.transactions.length} testId="activity-transactions">
              {data.transactions.length === 0 ? <p className="text-sm text-slate-400">Aucune transaction.</p> : (
                <div className="space-y-2">
                  {data.transactions.map((tx) => (
                    <div key={tx.id} className="flex items-center justify-between gap-3 text-sm py-2 border-b border-slate-100 last:border-0" data-testid={`activity-tx-${tx.id}`}>
                      <div className="min-w-0">
                        <p className="font-mono text-xs text-slate-700">{tx.payment_reference || tx.id}</p>
                        <p className="text-xs text-slate-500 truncate">{tx.lead_number ? `Lead ${tx.lead_number}` : "Lead"} · {fmtDate(tx.confirmed_at || tx.created_at)}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="font-semibold text-slate-900">{Number(tx.amount).toFixed(2)}€</span>
                        <TxStatus tx={tx} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
